import React, { useState, useCallback } from "react";
import { Box } from "@mui/material";
import ZoomOutMapOutlinedIcon from "@mui/icons-material/ZoomOutMapOutlined";
import { GalleryImage } from "./ImageGalleryTypes";
import ImageGalleryLightbox from "./ImageGalleryLightbox";
import { dt } from "../../themes/redesignTokens";

export interface ImagePreviewStripProps {
  images: GalleryImage[];
  maxVisible?: number;
  thumbnailSize?: number;
  gap?: number;
  borderRadius?: string;
}

const ImagePreviewStrip: React.FC<ImagePreviewStripProps> = ({
  images,
  maxVisible = 3,
  thumbnailSize = 56,
  gap = 8,
  borderRadius = "6px",
}) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const handleThumbnailClick = useCallback((index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  }, []);

  const handleMoreClick = useCallback(() => {
    setLightboxIndex(maxVisible);
    setLightboxOpen(true);
  }, [maxVisible]);

  const handleLightboxClose = useCallback(() => {
    setLightboxOpen(false);
  }, []);

  if (!images.length) return null;

  const visibleImages = images.slice(0, maxVisible);
  const remainingCount = Math.max(0, images.length - maxVisible);

  const thumbnailBaseSx = {
    width: thumbnailSize,
    height: thumbnailSize,
    borderRadius,
    overflow: "hidden",
    border: `1px solid ${dt.cardBorder}`,
    backgroundColor: dt.inputBg,
    cursor: "pointer",
    transition: "all 0.2s ease",
    flexShrink: 0,
    position: "relative" as const,
    "&:hover": {
      borderColor: dt.blue,
      boxShadow: `0 0 0 2px ${dt.blueGlow}`,
    },
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: `${gap}px`,
        }}
      >
        {visibleImages.map((image, index) => (
          <Box
            key={image.id}
            sx={thumbnailBaseSx}
            onClick={() => handleThumbnailClick(index)}
          >
            <Box
              component="img"
              src={image.thumbnailSrc || image.src}
              alt={image.alt || image.name || `Image ${index + 1}`}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
            {/* Zoom icon on first thumbnail */}
            {index === 0 && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: 3,
                  left: 3,
                  width: 20,
                  height: 20,
                  borderRadius: "4px",
                  backgroundColor: "rgba(0,0,0,0.55)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  pointerEvents: "none",
                }}
              >
                <ZoomOutMapOutlinedIcon
                  sx={{ fontSize: 13, color: "#FFFFFF" }}
                />
              </Box>
            )}
          </Box>
        ))}

        {/* "+N" overlay box */}
        {remainingCount > 0 && (
          <Box
            sx={{
              ...thumbnailBaseSx,
              position: "relative",
            }}
            onClick={handleMoreClick}
          >
            {/* Background image (4th image) */}
            <Box
              component="img"
              src={
                images[maxVisible]
                  ? images[maxVisible].thumbnailSrc || images[maxVisible].src
                  : undefined
              }
              alt=""
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
            {/* Dark overlay */}
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                backgroundColor: "rgba(0,0,0,0.55)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  color: "#FFFFFF",
                  fontFamily: dt.fontMono,
                  fontSize: "14px",
                  fontWeight: 700,
                  userSelect: "none",
                }}
              >
                +{remainingCount}
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {/* Lightbox — receives ALL images for full navigation */}
      <ImageGalleryLightbox
        images={images}
        open={lightboxOpen}
        onClose={handleLightboxClose}
        initialIndex={lightboxIndex}
      />
    </>
  );
};

export default ImagePreviewStrip;
